import { type ReactNode } from 'react'
import { fbt } from 'fbtee'
import invariant from 'tiny-invariant'
import { Skeleton } from './ui/skeleton'
import { MemberFacepile } from './MemberFacepile'
import { CancelledBadge } from './CancelledBadge'
import { PopularMark } from './PopularMark'
import { EventCardLineup } from './EventCardLineup'
import {
  ImageCard,
  ImageCardContent,
  ImageCardHeader,
  ImageCardMeta,
  ImageCardTitle,
} from './ImageCard'
import { formatEventDateRange } from '../lib/formatDate'
import { cn } from '../lib/utils'
import { useForceLoading } from '../lib/skeletonDebug'
import { MapPin, Calendar } from 'lucide-react'
import type { UserProfile } from '../types'
import type { TicketMarks } from '../hooks/useTicketMarksByEvent'

const EMPTY_GOING_SET: Set<string> = new Set()

export interface EventCardEvent {
  id: string
  name: string
  type: string
  location?: string
  venueName?: string
  startDate: string
  endDate?: string
  imageUrl?: string
  imageFocalX?: number
  imageFocalY?: number
  cancelled?: boolean
  cancelledReason?: string
}

interface Props {
  loading?: boolean
  facepileLoading?: boolean
  event?: EventCardEvent
  /** Names "Festival" on the date line; off where the list is already one type. */
  showType?: boolean
  /** Lineup names the title doesn't already spell out (see getLineupNames). */
  lineup?: string[]
  /** The viewer's followed artists on the lineup — lead the subtitle, highlighted. */
  followedArtists?: string[]
  /** Whole lineup's length, for "+N more" when `lineup` isn't loaded. */
  lineupSize?: number
  /** One of the region's most popular events. */
  popular?: boolean
  onClick?: () => void
  facepile?: { userIds: string[]; goingUserIds: Set<string>; pregameUserIds?: ReadonlySet<string>; tickets?: TicketMarks }
  profiles?: Record<string, UserProfile>
  currentUserId?: string | null
  locale?: string
  action?: ReactNode
  /** The viewer answered "Not going" — fades the card so it reads as dealt with. */
  declined?: boolean
  /** Appended to the date line, e.g. the event's pre-game time. */
  dateSuffix?: ReactNode
}

export function EventCard({
  loading: loadingProp,
  facepileLoading,
  event,
  showType = true,
  lineup = [],
  followedArtists = [],
  lineupSize,
  popular = false,
  dateSuffix,
  onClick,
  facepile,
  profiles = {},
  currentUserId,
  locale,
  action,
  declined = false,
}: Props) {
  const loading = useForceLoading(loadingProp)
  if (loading) {
    return (
      <ImageCard className="gap-2">
        <ImageCardHeader className="pb-0">
          <div className="min-w-0 space-y-1">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-5 w-14 rounded-full shrink-0" />
        </ImageCardHeader>
        <ImageCardContent>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
            <Skeleton className="h-3.5 w-1/2" />
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
            <Skeleton className="h-3.5 w-1/3" />
          </div>
        </ImageCardContent>
      </ImageCard>
    )
  }

  invariant(event, 'EventCard: event is required when not loading')
  invariant(onClick, 'EventCard: onClick is required when not loading')
  invariant(locale, 'EventCard: locale is required when not loading')

  const dateRange = formatEventDateRange(event.startDate, event.endDate, locale)
  const facepileUserIds = facepile?.userIds ?? []
  const facepileGoing = facepile?.goingUserIds ?? EMPTY_GOING_SET
  const goingMemberIds = facepileUserIds.filter(id => facepileGoing.has(id))
  const interestedMemberIds = facepileUserIds.filter(id => !facepileGoing.has(id))

  return (
    <ImageCard
      imageUrl={event.imageUrl}
      imageFocal={event}
      gradientSeed={!event.imageUrl ? event.id : undefined}
      className={cn(
        'gap-2 transition-[color,background-color,border-color,opacity]',
        declined && 'opacity-60 hover:opacity-100',
      )}
      aria-label={event.name}
      onClick={e => {
        if (!(e.currentTarget as HTMLElement).contains(e.target as Node)) return
        onClick()
      }}
    >
      <ImageCardHeader className="pb-0">
        <div className="min-w-0">
          <ImageCardTitle className={cn(event.cancelled && 'line-through opacity-70')}>
            {event.name}
          </ImageCardTitle>
          <EventCardLineup lineup={lineup} followed={followedArtists} lineupSize={lineupSize} eventName={event.name} />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {event.cancelled && <CancelledBadge reason={event.cancelledReason} />}
          {action}
        </div>
      </ImageCardHeader>
      <ImageCardContent>
        <ImageCardMeta icon={MapPin}>
          <span>{event.venueName ?? event.location}</span>
        </ImageCardMeta>
        <div className="flex min-h-6 items-center justify-between gap-3">
          <ImageCardMeta icon={Calendar} className="min-w-0">
            <span className="shrink-0">{dateRange}</span>
            {showType && event.type === 'festival' && (
              <>
                <span aria-hidden>·</span>
                <span className="shrink-0">{fbt('Festival', "Marker on an event card's date line saying the event is a festival")}</span>
              </>
            )}
            {popular && !event.cancelled && (
              <>
                <span aria-hidden>·</span>
                <PopularMark />
              </>
            )}
            {dateSuffix && <span aria-hidden>·</span>}
            {dateSuffix}
          </ImageCardMeta>
          <MemberFacepile
            loading={facepileLoading}
            interestedMemberIds={interestedMemberIds}
            goingMemberIds={goingMemberIds}
            profiles={profiles}
            currentUserId={currentUserId}
            dialogTitle={event.name}
            historyKey={`event:${event.id}`}
            pregameGoerIds={facepile?.pregameUserIds}
            tickets={facepile?.tickets}
          />
        </div>
      </ImageCardContent>
    </ImageCard>
  )
}
